import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { it as itLocale } from 'date-fns/locale';
import { toast } from 'sonner';
import {
    Loader2, Trash2, Users, Wallet, StickyNote,
    Search, Armchair, LayoutGrid, ListFilter, Download, CheckCircle2,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { api } from '@/lib/api';
import { TEST_IDS } from '@/constants/testIds/floreal';

// --- Booking status metadata (theme-consistent, no raw red/green) ---
const STATUS_ORDER = ['in_attesa_acconto', 'confermata', 'annullata', 'completata'];
const STATUS_META = {
    in_attesa_acconto: { label: 'In attesa di acconto', badge: 'border-atelier-clay bg-atelier-clay/30 text-atelier-bronze' },
    confermata: { label: 'Acconto ricevuto / Confermata', badge: 'border-primary/40 bg-primary/15 text-primary' },
    annullata: { label: 'Annullata', badge: 'border-border bg-muted text-muted-foreground line-through' },
    completata: { label: 'Completata', badge: 'border-atelier-sage/50 bg-atelier-sage/15 text-atelier-sage-ink' },
};
const statusLabel = (v) => (STATUS_META[v] || STATUS_META.in_attesa_acconto).label;

const eur = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const money = (v) => (v == null ? '—' : eur.format(v));

const safeDate = (iso, pattern) => {
    try { return format(new Date(iso), pattern, { locale: itLocale }); } catch { return iso || '—'; }
};
const eventDateLabel = (ev) => {
    if (!ev || !ev.date) return 'Data da definire';
    let d;
    try { d = format(new Date(`${ev.date}T00:00:00`), 'EEEE d MMMM yyyy', { locale: itLocale }); } catch { d = ev.date; }
    const time = ev.start_time ? ` · ${ev.start_time}${ev.end_time ? `–${ev.end_time}` : ''}` : '';
    return `${d}${time}`;
};
const typeLabel = (t) => (t === 'aperiworkshop' ? 'Aperiworkshop' : 'Workshop');

export default function RegistrationsPanel() {
    const [events, setEvents] = useState([]);
    const [regs, setRegs] = useState([]);
    const [loading, setLoading] = useState(true);

    // notes/reference dialog state
    const [notesReg, setNotesReg] = useState(null);
    const [notesValue, setNotesValue] = useState('');
    const [refValue, setRefValue] = useState('');
    const [savingNotes, setSavingNotes] = useState(false);

    // summary filters
    const [fWorkshop, setFWorkshop] = useState('all');
    const [fDate, setFDate] = useState('');
    const [fStatus, setFStatus] = useState('all');
    const [fName, setFName] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const [evRes, regRes] = await Promise.all([
                api.get('/admin/events'),
                api.get('/admin/registrations'),
            ]);
            setEvents(evRes.data || []);
            setRegs(regRes.data || []);
        } catch {
            toast.error('Impossibile caricare le prenotazioni');
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const eventMap = useMemo(() => {
        const m = {};
        events.forEach((e) => { m[e.id] = e; });
        return m;
    }, [events]);

    // Group bookings by workshop (event). Include events with 0 bookings,
    // and synthesize a group for bookings whose event was deleted.
    const groups = useMemo(() => {
        const byId = {};
        events.forEach((e) => { byId[e.id] = { event: e, regs: [] }; });
        regs.forEach((r) => {
            if (!byId[r.event_id]) {
                byId[r.event_id] = {
                    event: { id: r.event_id, title: r.event_title || 'Workshop rimosso', date: r.event_date || null, total_seats: null, _orphan: true },
                    regs: [],
                };
            }
            byId[r.event_id].regs.push(r);
        });
        const arr = Object.values(byId);
        arr.sort((a, b) => (a.event.date || '9999-99-99').localeCompare(b.event.date || '9999-99-99'));
        return arr;
    }, [events, regs]);

    const statsFor = (group) => {
        const list = group.regs;
        const active = list.filter((r) => r.status !== 'annullata');
        const occupied = active.reduce((s, r) => s + (r.num_participants || 0), 0);
        const total = group.event.total_seats;
        const available = total != null ? Math.max(0, total - occupied) : null;
        const confirmed = list.filter((r) => r.status === 'confermata' || r.status === 'completata');
        const waiting = list.filter((r) => r.status === 'in_attesa_acconto');
        const collected = confirmed.reduce((s, r) => s + (r.amount_total != null ? r.amount_total : (r.price_per_person || 0) * (r.num_participants || 0)), 0);
        return {
            activeCount: active.length, occupied, total, available,
            confirmedCount: confirmed.length, waitingCount: waiting.length,
            collected, count: list.length,
        };
    };

    // ---- mutations ----
    const updateStatus = async (reg, value) => {
        const prev = reg.status;
        if (value === prev) return;
        setRegs((rs) => rs.map((r) => (r.id === reg.id ? { ...r, status: value } : r)));
        try {
            await api.put(`/admin/registrations/${reg.id}`, { status: value });
            toast.success(`Stato aggiornato: ${statusLabel(value)}`);
        } catch {
            setRegs((rs) => rs.map((r) => (r.id === reg.id ? { ...r, status: prev } : r)));
            toast.error('Errore aggiornamento stato');
        }
    };

    const openNotes = (reg) => { setNotesReg(reg); setNotesValue(reg.internal_notes || ''); setRefValue(reg.payment_reference || ''); };
    const saveNotes = async () => {
        if (!notesReg) return;
        setSavingNotes(true);
        try {
            await api.put(`/admin/registrations/${notesReg.id}`, { internal_notes: notesValue, payment_reference: refValue });
            setRegs((rs) => rs.map((r) => (r.id === notesReg.id ? { ...r, internal_notes: notesValue, payment_reference: refValue } : r)));
            toast.success('Note salvate');
            setNotesReg(null);
        } catch {
            toast.error('Errore salvataggio note');
        } finally {
            setSavingNotes(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Eliminare questa prenotazione? I posti verranno restituiti al workshop.')) return;
        try {
            await api.delete(`/admin/registrations/${id}`);
            setRegs((rs) => rs.filter((r) => r.id !== id));
            toast.success('Prenotazione eliminata');
        } catch { toast.error('Errore eliminazione'); }
    };

    // ---- CSV export (Excel-friendly: ; separator + UTF-8 BOM) ----
    const csvEscape = (val) => {
        const s = val == null ? '' : String(val);
        return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const buildCsv = (rows) => {
        const headers = ['Workshop', 'Tipologia', 'Data workshop', 'Nome', 'Cognome', 'Email', 'Telefono', 'N. partecipanti', 'Prezzo p.p.', 'Importo totale', 'Pagamento entro', 'Stato', 'Data prenotazione', 'Rif. pagamento', 'Note interne'];
        const lines = [headers.join(';')];
        rows.forEach((r) => {
            const ev = eventMap[r.event_id];
            const wsTitle = ev?.title || r.event_title || 'Workshop rimosso';
            const wsDate = (r.event_date || ev?.date) ? safeDate(`${r.event_date || ev.date}T00:00:00`, 'dd/MM/yyyy') : '';
            const amount = r.amount_total != null ? r.amount_total : (r.price_per_person != null ? r.price_per_person * r.num_participants : '');
            const due = r.payment_due_date ? safeDate(`${r.payment_due_date}T00:00:00`, 'dd/MM/yyyy') : '';
            lines.push([
                wsTitle, typeLabel(r.event_type || ev?.event_type), wsDate,
                r.first_name || '', r.last_name || (r.first_name ? '' : r.full_name), r.email, r.phone, r.num_participants,
                r.price_per_person != null ? r.price_per_person : '', amount, due, statusLabel(r.status),
                safeDate(r.created_at, 'dd/MM/yyyy'), r.payment_reference || '', r.internal_notes || '',
            ].map(csvEscape).join(';'));
        });
        return `\uFEFF${lines.join('\r\n')}`;
    };
    const slug = (s) => (s || 'workshop').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '').slice(0, 40);
    const exportRows = (rows, name) => {
        if (!rows.length) { toast.error('Nessuna prenotazione da esportare'); return; }
        const content = buildCsv(rows);
        const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${name}_${format(new Date(), 'yyyy-MM-dd')}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(`${rows.length} prenotazioni esportate`);
    };

    // ---- reusable cells ----
    const StatusSelect = ({ reg }) => (
        <Select value={reg.status || 'in_attesa_acconto'} onValueChange={(v) => updateStatus(reg, v)}>
            <SelectTrigger data-testid={TEST_IDS.adminWorkshopPaymentSelect} className="h-8 w-[210px] text-xs">
                <SelectValue />
            </SelectTrigger>
            <SelectContent>
                {STATUS_ORDER.map((s) => (
                    <SelectItem key={s} value={s} className="text-xs">{STATUS_META[s].label}</SelectItem>
                ))}
            </SelectContent>
        </Select>
    );

    const NotesButton = ({ reg }) => {
        const has = reg.internal_notes || reg.payment_reference;
        return (
            <Button
                variant="ghost"
                size="sm"
                className={`h-8 px-2 text-xs ${has ? 'text-atelier-bronze' : 'text-muted-foreground'}`}
                onClick={() => openNotes(reg)}
                data-testid={TEST_IDS.adminWorkshopNotesButton}
                title={reg.payment_reference ? `Rif: ${reg.payment_reference}` : 'Note / riferimento pagamento'}
            >
                <StickyNote className="h-3.5 w-3.5 mr-1" />
                {has ? 'Note' : 'Aggiungi'}
            </Button>
        );
    };

    const totalRegs = regs.length;

    // ---- summary (flat) rows with filters ----
    const summaryRows = useMemo(() => {
        const rows = regs.map((r) => {
            const ev = eventMap[r.event_id];
            return { ...r, _eventDate: r.event_date || ev?.date || null, _eventTitle: ev?.title || r.event_title || 'Workshop rimosso' };
        });
        const name = fName.trim().toLowerCase();
        return rows.filter((r) => {
            if (fWorkshop !== 'all' && r.event_id !== fWorkshop) return false;
            if (fDate && r._eventDate !== fDate) return false;
            if (fStatus !== 'all' && (r.status || 'in_attesa_acconto') !== fStatus) return false;
            if (name && !(`${r.full_name}`.toLowerCase().includes(name) || `${r.email}`.toLowerCase().includes(name))) return false;
            return true;
        }).sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
    }, [regs, eventMap, fWorkshop, fDate, fStatus, fName]);

    if (loading) {
        return (
            <div className="rounded-xl border border-border bg-card px-5 py-16 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Caricamento prenotazioni…
            </div>
        );
    }

    return (
        <div data-testid={TEST_IDS.adminWorkshopSignups} className="space-y-5">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="font-serif text-2xl">Prenotazioni Workshop</h2>
                    <p className="text-sm text-muted-foreground mt-1">
                        Gestione ordinata, divisa per singolo workshop: chi è iscritto, chi ha versato l’acconto e i posti disponibili.
                    </p>
                </div>
                <span className="text-xs text-muted-foreground">{totalRegs} prenotazioni totali · {groups.length} workshop</span>
            </div>

            {/* Status legend */}
            <div className="flex flex-wrap gap-2">
                {STATUS_ORDER.map((s) => (
                    <Badge key={s} variant="outline" className={`text-[11px] font-normal ${STATUS_META[s].badge}`}>
                        {STATUS_META[s].label}
                    </Badge>
                ))}
            </div>

            <Tabs defaultValue="byworkshop" className="w-full">
                <TabsList data-testid={TEST_IDS.adminWorkshopViewToggle}>
                    <TabsTrigger value="byworkshop" data-testid="admin-workshop-tab-grouped">
                        <LayoutGrid className="h-4 w-4 mr-2" />Per workshop
                    </TabsTrigger>
                    <TabsTrigger value="summary" data-testid="admin-workshop-tab-summary">
                        <ListFilter className="h-4 w-4 mr-2" />Riepilogo generale
                    </TabsTrigger>
                </TabsList>

                {/* ============ PER WORKSHOP ============ */}
                <TabsContent value="byworkshop" className="mt-5">
                    {groups.length === 0 ? (
                        <div className="rounded-xl border border-border bg-card px-5 py-14 text-center text-muted-foreground">
                            Nessun workshop presente. Crea un evento nella scheda «Eventi».
                        </div>
                    ) : (
                        <Accordion type="multiple" className="space-y-3" data-testid={TEST_IDS.adminWorkshopAccordion}>
                            {groups.map((g) => {
                                const st = statsFor(g);
                                const seatLabel = st.total != null ? `${st.occupied}/${st.total} posti` : `${st.occupied} partecipanti`;
                                return (
                                    <AccordionItem
                                        key={g.event.id}
                                        value={g.event.id}
                                        data-testid={TEST_IDS.adminWorkshopGroup}
                                        className="border border-border rounded-xl bg-card overflow-hidden px-0"
                                    >
                                        <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-atelier-cream/40">
                                            <div className="flex flex-1 flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-left pr-2">
                                                <div className="min-w-0 flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <span className="font-serif text-lg leading-tight truncate">{g.event.title}</span>
                                                        <Badge variant="outline" className="text-[10px] font-normal shrink-0">{typeLabel(g.event.event_type)}</Badge>
                                                    </div>
                                                    <div className="text-xs text-muted-foreground mt-0.5 capitalize">{eventDateLabel(g.event)}</div>
                                                </div>
                                                <div className="flex flex-wrap items-center gap-1.5">
                                                    <Badge variant="outline" className="text-[11px] font-normal border-atelier-sage/50 bg-atelier-sage/10 text-atelier-sage-ink">
                                                        <Users className="h-3 w-3 mr-1" />{st.activeCount} iscritti
                                                    </Badge>
                                                    <Badge variant="outline" className="text-[11px] font-normal border-border">
                                                        <Armchair className="h-3 w-3 mr-1" />{seatLabel}
                                                    </Badge>
                                                    {st.waitingCount > 0 && (
                                                        <Badge variant="outline" className={`text-[11px] font-normal ${STATUS_META.in_attesa_acconto.badge}`}>
                                                            {st.waitingCount} in attesa
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        </AccordionTrigger>
                                        <AccordionContent className="px-0 pb-0">
                                            {/* stats strip */}
                                            <div className="grid grid-cols-2 md:grid-cols-5 gap-px bg-border border-y border-border">
                                                <Stat icon={Armchair} label="Posti max" value={st.total != null ? st.total : '—'} />
                                                <Stat icon={Users} label="Iscritti (partec.)" value={`${st.activeCount} (${st.occupied})`} />
                                                <Stat icon={Armchair} label="Posti disponibili" value={st.available != null ? st.available : '—'} highlight={st.available === 0} />
                                                <Stat icon={CheckCircle2} label="Confermate" value={st.confirmedCount} sub={`${st.waitingCount} in attesa`} />
                                                <Stat icon={Wallet} label="Importi incassati" value={money(st.collected)} />
                                            </div>

                                            {g.regs.length === 0 ? (
                                                <div className="px-5 py-10 text-center text-sm text-muted-foreground">Nessuna prenotazione per questo workshop.</div>
                                            ) : (
                                                <>
                                                    <div className="flex justify-end px-4 pt-3">
                                                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => exportRows(g.regs, `prenotazioni_${slug(g.event.title)}`)} data-testid={TEST_IDS.adminWorkshopExportGroup}>
                                                            <Download className="h-3.5 w-3.5 mr-1.5" />Scarica iscritti
                                                        </Button>
                                                    </div>
                                                    <div className="overflow-x-auto">
                                                        <Table data-testid={TEST_IDS.adminWorkshopTable}>
                                                            <TableHeader>
                                                                <TableRow>
                                                                    <TableHead>Nome e cognome</TableHead>
                                                                    <TableHead>Contatti</TableHead>
                                                                    <TableHead>Iscrizione</TableHead>
                                                                    <TableHead className="text-center">Partec.</TableHead>
                                                                    <TableHead>Prezzo p.p.</TableHead>
                                                                    <TableHead>Importo totale</TableHead>
                                                                    <TableHead>Stato</TableHead>
                                                                    <TableHead>Note / Rif.</TableHead>
                                                                    <TableHead className="text-right">Azioni</TableHead>
                                                                </TableRow>
                                                            </TableHeader>
                                                            <TableBody>
                                                                {g.regs.map((r) => (
                                                                    <TableRow key={r.id} className={r.status === 'annullata' ? 'opacity-60' : ''}>
                                                                        <TableCell>
                                                                            <div className={`font-medium ${r.status === 'annullata' ? 'line-through' : ''}`}>{r.full_name}</div>
                                                                            {r.notes ? <div className="text-[11px] text-muted-foreground mt-0.5 max-w-[200px] line-clamp-2">Cliente: {r.notes}</div> : null}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            <div className="text-xs">{r.email}</div>
                                                                            <div className="text-xs text-muted-foreground">{r.phone}</div>
                                                                        </TableCell>
                                                                        <TableCell><span className="text-xs text-muted-foreground">{safeDate(r.created_at, 'd MMM yyyy')}</span></TableCell>
                                                                        <TableCell className="text-center">{r.num_participants}</TableCell>
                                                                        <TableCell className="text-xs">{money(r.price_per_person)}</TableCell>
                                                                        <TableCell className="font-medium">{money(r.amount_total != null ? r.amount_total : (r.price_per_person != null ? r.price_per_person * r.num_participants : null))}</TableCell>
                                                                        <TableCell><StatusSelect reg={r} /></TableCell>
                                                                        <TableCell><NotesButton reg={r} /></TableCell>
                                                                        <TableCell className="text-right">
                                                                            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(r.id)}>
                                                                                <Trash2 className="h-4 w-4" />
                                                                            </Button>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </div>
                                                </>
                                            )}
                                        </AccordionContent>
                                    </AccordionItem>
                                );
                            })}
                        </Accordion>
                    )}
                </TabsContent>

                {/* ============ RIEPILOGO GENERALE ============ */}
                <TabsContent value="summary" className="mt-5">
                    <div className="rounded-xl border border-border bg-card overflow-hidden">
                        <div className="p-4 border-b border-border grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                            <div>
                                <Label className="text-xs text-muted-foreground">Workshop</Label>
                                <Select value={fWorkshop} onValueChange={setFWorkshop}>
                                    <SelectTrigger data-testid={TEST_IDS.adminWorkshopFilterWorkshop} className="h-9 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tutti i workshop</SelectItem>
                                        {groups.map((g) => (<SelectItem key={g.event.id} value={g.event.id}>{g.event.title}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Data workshop</Label>
                                <Input type="date" value={fDate} onChange={(e) => setFDate(e.target.value)} className="h-9 mt-1" data-testid={TEST_IDS.adminWorkshopFilterDate} />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Stato</Label>
                                <Select value={fStatus} onValueChange={setFStatus}>
                                    <SelectTrigger data-testid={TEST_IDS.adminWorkshopFilterPayment} className="h-9 mt-1"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tutti gli stati</SelectItem>
                                        {STATUS_ORDER.map((s) => (<SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Cliente</Label>
                                <div className="relative mt-1">
                                    <Search className="h-4 w-4 text-muted-foreground absolute left-2.5 top-1/2 -translate-y-1/2" />
                                    <Input value={fName} onChange={(e) => setFName(e.target.value)} placeholder="Nome o email…" className="h-9 pl-8" data-testid={TEST_IDS.adminWorkshopFilterName} />
                                </div>
                            </div>
                        </div>

                        <div className="px-4 py-2 border-b border-border flex items-center justify-between gap-2">
                            <span className="text-xs text-muted-foreground">{summaryRows.length} risultati</span>
                            <div className="flex items-center gap-2">
                                {(fWorkshop !== 'all' || fDate || fStatus !== 'all' || fName) && (
                                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => { setFWorkshop('all'); setFDate(''); setFStatus('all'); setFName(''); }}>
                                        Azzera filtri
                                    </Button>
                                )}
                                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={() => exportRows(summaryRows, 'prenotazioni_workshop')} data-testid={TEST_IDS.adminWorkshopExport}>
                                    <Download className="h-3.5 w-3.5 mr-1.5" />Scarica iscritti
                                </Button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <Table data-testid={TEST_IDS.adminWorkshopSummaryTable}>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Cliente</TableHead>
                                        <TableHead>Contatti</TableHead>
                                        <TableHead>Workshop</TableHead>
                                        <TableHead>Tipo</TableHead>
                                        <TableHead>Data workshop</TableHead>
                                        <TableHead className="text-center">Partec.</TableHead>
                                        <TableHead>Importo tot.</TableHead>
                                        <TableHead>Iscrizione</TableHead>
                                        <TableHead>Stato</TableHead>
                                        <TableHead>Note</TableHead>
                                        <TableHead className="text-right">Azioni</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {summaryRows.length === 0 ? (
                                        <TableRow><TableCell colSpan={11} className="py-12 text-center text-muted-foreground">Nessuna prenotazione corrisponde ai filtri.</TableCell></TableRow>
                                    ) : summaryRows.map((r) => (
                                        <TableRow key={r.id} className={r.status === 'annullata' ? 'opacity-60' : ''}>
                                            <TableCell><div className={`font-medium ${r.status === 'annullata' ? 'line-through' : ''}`}>{r.full_name}</div></TableCell>
                                            <TableCell><div className="text-xs">{r.email}</div><div className="text-xs text-muted-foreground">{r.phone}</div></TableCell>
                                            <TableCell><span className="text-sm">{r._eventTitle}</span></TableCell>
                                            <TableCell><span className="text-xs">{typeLabel(r.event_type)}</span></TableCell>
                                            <TableCell><span className="text-xs text-muted-foreground">{r._eventDate ? safeDate(`${r._eventDate}T00:00:00`, 'd MMM yyyy') : '—'}</span></TableCell>
                                            <TableCell className="text-center">{r.num_participants}</TableCell>
                                            <TableCell className="text-xs">{money(r.amount_total != null ? r.amount_total : (r.price_per_person != null ? r.price_per_person * r.num_participants : null))}</TableCell>
                                            <TableCell><span className="text-xs text-muted-foreground">{safeDate(r.created_at, 'd MMM yyyy')}</span></TableCell>
                                            <TableCell><StatusSelect reg={r} /></TableCell>
                                            <TableCell><NotesButton reg={r} /></TableCell>
                                            <TableCell className="text-right">
                                                <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" onClick={() => remove(r.id)}>
                                                    <Trash2 className="h-4 w-4" />
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </TabsContent>
            </Tabs>

            {/* Notes + payment reference dialog */}
            <Dialog open={!!notesReg} onOpenChange={(o) => { if (!o) setNotesReg(null); }}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-serif">Note e riferimento pagamento</DialogTitle>
                    </DialogHeader>
                    {notesReg && (
                        <div className="space-y-3">
                            <div className="text-sm">
                                <span className="font-medium">{notesReg.full_name}</span>
                                <span className="text-muted-foreground"> · {eventMap[notesReg.event_id]?.title || notesReg.event_title || 'Workshop'}</span>
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Riferimento pagamento / contabile</Label>
                                <Input value={refValue} onChange={(e) => setRefValue(e.target.value)} className="mt-1" placeholder="Es. Bonifico ricevuto 12/05 — rif. #1234" />
                            </div>
                            <div>
                                <Label className="text-xs text-muted-foreground">Note interne</Label>
                                <Textarea value={notesValue} onChange={(e) => setNotesValue(e.target.value)} rows={4} className="mt-1" placeholder="Appunti visibili solo allo staff…" />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setNotesReg(null)}>Annulla</Button>
                        <Button onClick={saveNotes} disabled={savingNotes} data-testid={TEST_IDS.adminWorkshopNotesSave}>
                            {savingNotes && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Salva
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const Stat = ({ icon: Icon, label, value, sub, highlight }) => (
    <div className="bg-card px-4 py-3">
        <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
            <Icon className="h-3.5 w-3.5" />{label}
        </div>
        <div className={`mt-1 font-serif text-lg ${highlight ? 'text-destructive' : ''}`}>{value}</div>
        {sub ? <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div> : null}
    </div>
);
